import { useCallback, useEffect } from "react";
import { useDropArea } from "react-use";
import { useToasts } from "@puzzle/ui";
import {
  SupportedEntity,
  SupportedUploadEntity,
  useAttachmentManager,
} from "../hooks/useAttachmentManager/useAttachmentManager";
import { isSupportedFileType } from "../hooks/useAttachmentManager/utils";
import { attachmentSidebarText } from "./constants";

type AttachmentSidebarProps = {
  entity?: SupportedEntity;
  uploadEntityType: SupportedUploadEntity;
  inMemoryFile?: File;
  setInMemoryFile: (file?: File) => void;
  isEditor: boolean;
};
/**
 * This is a UI specific hook that handles the file input ref,
 * drop area functionality, preview file url, and drop area state
 */
export const useAttachmentSidebar = ({
  entity,
  uploadEntityType,
  inMemoryFile,
  setInMemoryFile,
  isEditor,
}: AttachmentSidebarProps) => {
  const { toast } = useToasts();

  const {
    previewUrl: existingPreviewUrl,
    uploadFile,
    deleteFile,
    isUploading,
    handleUnlinkFile,
  } = useAttachmentManager({
    entity,
    uploadEntityType,
    isEditor,
  });

  const handleFiles = useCallback(
    (files: File[] | FileList) => {
      if (!isEditor) return;
      const fileArray = Array.isArray(files) ? files : Array.from(files);
      if (fileArray[0] && !isSupportedFileType(fileArray[0])) {
        toast({ status: "warning", message: attachmentSidebarText.pdfWarningMessage });
        return;
      }

      // Entity doesn't exist yet, preview in memory and upload when they post
      if (!entity) {
        setInMemoryFile(fileArray[0]);
        return;
      }

      // Entity already exists, upload the file
      uploadFile(fileArray);
    },
    [entity, uploadFile, setInMemoryFile, isEditor, toast]
  );

  const onDelete = useCallback(() => {
    if (inMemoryFile) {
      setInMemoryFile(undefined);
    } else {
      deleteFile();
    }
  }, [deleteFile, inMemoryFile, setInMemoryFile]);

  const [dropAreaProps, { over }] = useDropArea({
    onFiles: handleFiles,
  });

  const objectUrl = inMemoryFile ? URL.createObjectURL(inMemoryFile) : null;
  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const previewFileUrl = objectUrl || existingPreviewUrl;

  return {
    handleFiles,
    onDelete,
    dropAreaProps,
    over,
    previewFileUrl,
    isUploading,
    handleUnlinkFile,
  };
};
